<template>
  <div >
    <div >
      <!-- Loading -->
      <div v-if="isLoading" :class="embedded ? 'comments-loading-embedded' : 'comments-loading'">
        <div class="loading-spinner">
          <div class="loading-c d-block mx-auto" />
        </div>
      </div>

      <!-- Comments list -->
      <template v-else-if="comments && comments.length">
        <div v-if="!embedded" class="comments-header">
          <h5 class="comments-title">
            {{ getCommentsTitle() }}
          </h5>
        </div>
        
        <div :class="embedded ? 'comments-content-embedded' : 'comments-content'">
          <comment-item
            v-for="(comment, index) in comments"
            :key="comment.key"
            :comment="comment"
            :class="{ 'comment-item-spacing': index > 0 && !embedded, 'comment-item-spacing-embedded': index > 0 && embedded }"
            :leaf="leaf"
            :item-type="itemType"
            :morph-type="morphType"
            :source-key="sourceKey"
            :can-moderate="canModerate"
            :comment-highlighted="commentKeyHighlighted === comment.key"
            :sub-comment-key-highlighted="subCommentKeyHighlighted"
            @toggle-like="handleToggleLike"
            @commented="handleCommented"
            @deleted="handleDeleteCommented"
            @moderation-complete="handleModerationComplete"
          />
        </div>
      </template>

      <!-- No comments yet -->
      <div v-else :class="embedded ? 'comments-empty-embedded' : 'comments-empty'">
        <div class="empty-content">
          <b-img center :src="Commentsplaceholder" class="placeholder-size" />
          <p class="empty-message">
            {{ $t('social.no-comments') }}
          </p>
        </div>
      </div>

      <!-- Load more comments -->
      <div
        v-if="hasMore"
        :class="embedded ? 'comments-actions-embedded' : 'comments-actions'"
      >
        <button
          type="button"
          class="btn btn-load-more"
          :disabled="isLoadingNextPage"
          @click="handleLoadNextPage"
        >
          <b-spinner 
            v-if="isLoadingNextPage" 
            small 
            class="mr-1" 
          /> 
          {{ $t('action.load-more') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BImg, BSpinner, BButton } from 'bootstrap-vue';
import CommentsPlaceholder from '@/assets/images/placeholders/light/comments.svg';
import ToastificationContentVue from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CommentsList',
  components: {
    // this avoids problems with vue circular references.
    // @see https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    CommentItem: () => import('./CommentItem.vue'),
    BImg,
    BSpinner,
    BButton,
  },
  props: {
    sourceKey: {
      type: String,
      required: true,
    },
    morphType: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      default: null,
    },
    leaf: Boolean,
    canModerate: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
    /**
     * The key of the comment to highlight
     */
    commentKeyHighlighted: {
      type: String,
      default: null,
    },
    /**
     * The key of the sub-comment to highlight (for nested comments)
     */
    subCommentKeyHighlighted: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentPage: 1,
      isLoading: false,
      isLoadingNextPage: false,
      commentsUpdated: false,
    };
  },
  computed: {
    Commentsplaceholder() {
      return CommentsPlaceholder;
    },
    // Helper to get the correct storedKey for this context
    storedKey() {
      return this.leaf
        ? this.sourceKey
        : `comments-${this.morphType}-${this.sourceKey}`;
    },
    commentsData() {
      return this.$store.getters.comments;
    },
    hasCommentsForSourceKey() {
      return this.leaf && this.commentsData && this.commentsData[this.sourceKey];
    },
    hasCommentsForTopLevelComments() {
      return !this.leaf && this.commentsData && this.commentsData[this.storedKey];
    },
    commentsForSourceKey() {
      const arr = this.commentsData[this.sourceKey]?.unpaginated || [];
      console.log('commentsForSourceKey:', arr);
      return arr.slice().reverse();
    },
    commentsForTopLevelComments() {
      const arr = this.commentsData[this.storedKey]?.unpaginated || [];
      console.log('commentsForTopLevelComments:', arr);
      return arr.slice().reverse();
    },
    totalCommentsForSourceKey() {
      return this.commentsData[this.sourceKey]?.meta?.total || 0;
    },
    totalCommentsForTopLevelComments() {
      return this.commentsData[this.storedKey]?.meta?.total || 0;
    },
    comments() {
      // For nested comments (replies to a comment), use the sourceKey as storedKey
      if (this.hasCommentsForSourceKey) {
        return this.commentsForSourceKey;
      }
      // For top-level comments, use the unique storedKey
      if (this.hasCommentsForTopLevelComments) {
        return this.commentsForTopLevelComments;
      }
      return [];
    },
    totalComments() {
      // For nested comments, get total from specific sourceKey
      if (this.hasCommentsForSourceKey) {
        return this.totalCommentsForSourceKey;
      }
      // For top-level comments, get total from unique storedKey
      if (this.hasCommentsForTopLevelComments) {
        return this.totalCommentsForTopLevelComments;
      }
      return 0;
    },
    hasMore() {
      // For nested comments, check the specific sourceKey
      if (this.hasCommentsForSourceKey) {
        const total = this.totalCommentsForSourceKey;
        return this.comments && this.comments.length < total;
      }
      // For top-level comments, check the unique storedKey
      if (this.hasCommentsForTopLevelComments) {
        const total = this.totalCommentsForTopLevelComments;
        return this.comments && this.comments.length < total;
      }
      return false;
    },
  },
  async created() {
    console.log('CommentsList created with sourceKey:', this.sourceKey);
    this.isLoading = true;
    try {
      console.log('fetchData called in created');
      await this.fetchData();
    } catch (error) {
      this.handleError();
    } finally {
      this.isLoading = false;
    }
  },
  watch: {
    sourceKey: {
      immediate: false,
      handler(newVal, oldVal) {
        console.log('sourceKey changed', newVal, oldVal);
        this.currentPage = 1;
        this.isLoading = true;
        this.fetchData()
          .catch(this.handleError)
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    commentsUpdated: {
      immediate: true,
      handler(newVal) {
        // console.log('commentsUpdated changed', newVal);
      }
    },
    refreshKey() {
      console.log('refreshKey changed', this.refreshKey);
      this.fetchData();
    },
  },
  methods: {
    async handleLoadNextPage() {
      if (this.isLoadingNextPage) {
        return;
      }

      try {
        this.currentPage += 1;
        this.isLoadingNextPage = true;
        await this.fetchData();
      } catch (error) {
        this.handleError();
        this.currentPage -= 1; // Revert page on error
      } finally {
        this.isLoadingNextPage = false;
      }
    },
    async handleToggleLike(comment) {
      try {
        await this.$store.dispatch('toggleLike', {
          itemType: 'comments',
          storedKey: this.sourceKey,
          key: comment.key,
          morphType: 'comment',
        });
      } catch (error) {
        this.handleError();
      }
    },
    async fetchData() {
      console.log('fetchData called with sourceKey:', this.sourceKey);

      this.commentsUpdated = false;

      // For nested comments (replies), use the sourceKey as storedKey
      // For top-level comments, use a unique storedKey based on morphType and sourceKey
      const storedKey = this.leaf
        ? this.sourceKey
        : `comments-${this.morphType}-${this.sourceKey}`;
      // console.log('storedKey', storedKey);
      
      await this.$store.dispatch('getItems', {
        itemType: 'comments',
        storedKey: storedKey,
        page: this.currentPage,
        perPage: 16,
        forceAPICall: true,
        requestConfig: {
          orderByDate: -1,
          morphType: this.morphType,
          key: this.sourceKey,
        },
      });

      // Update the commentsData with the new comments
      // this.$store.commit('setComments', {
      //   storedKey: storedKey,
      //   comments: this.comments,
      // });

      this.commentsUpdated = true;
      
    },
    handleCommented(targetComment) {
      if (targetComment && targetComment.childCommentsCount !== undefined) {
        ++targetComment.childCommentsCount;
      }
      this.$emit('commented', targetComment);
    },
    async handleModerationComplete({ action, contentKey, response }) {
      // console.log('handleModerationComplete', action, contentKey);
      
      if (action === 'reject') {
        await this.handleDeleteCommented();
      }
      
      // Refresh comments after moderation
      await this.fetchData();
      
      this.$emit('moderation-complete', {
        action: action,
        contentKey: contentKey,
        response: response
      });
    },
    handleDeleteCommented() {
      if (this.leaf) {
        this.$emit('delete');
      } else {
        this.$emit('delete-comment');
      }
    },
    getCommentsTitle() {
      return this.$t('social.comments-plural');

      // const count = this.totalComments;
      
      // // Handle pluralization based on current locale
      // const locale = this.$i18n.locale;
      
      // if (count === 0) {
      //   return this.$t('social.no-comments');
      // }

      // // use the comments-singular or comments-plural
      // const commentsTitle = count === 1 ? this.$t('social.comments-singular') : this.$t('social.comments-plural');
      // return `${count} ${commentsTitle}`;
    },
    handleError() {
      this.$toast({
        component: ToastificationContentVue,
        props: {
          title: this.$t('error-message.general-error'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-include';

.comments-list-wrapper {
  margin-bottom: 1.5rem;
}

.comments-list-container {
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
}

.comments-header {
  padding: 1.25rem 1.5rem 0.75rem;
  border-bottom: 1px solid #f0f2f5;
}

.comments-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #6f767f;
  letter-spacing: 0.5px;
}

.comments-content {
  padding: 1rem 1.5rem;
}

.comment-item-spacing {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f2f5;
}

.comments-loading {
  padding: 2rem 1.5rem;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
}

.loading-c {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 6px solid #f2c200; 
  border-right-color: transparent;
  border-bottom-color: transparent;
  animation: loading-spin 0.9s linear infinite;
}

@keyframes loading-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.comments-empty {
  padding: 2rem 1.5rem;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  width: 100%;
}

.placeholder-size {
  max-width: 120px;
  margin-bottom: 1rem;
}

.comments-empty-embedded .placeholder-size {
  max-width: 60px;
  margin-bottom: 0.5rem;
}

.empty-message {
  color: #6f767f;
  font-weight: 500;
  text-align: center;
  margin: 0;
}

.comments-empty-embedded .empty-message {
  color: #adb5bd;
  font-size: 0.875rem;
  font-weight: 400;
}

.comments-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 1.5rem 1.25rem;
  background-color: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

.btn-load-more {
  padding: 0.5rem 1.25rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background-color: #f8f9fa;
    color: #495057;
    border-color: #adb5bd;
  }
  
  &:active:not(:disabled) {
    background-color: #e9ecef;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Embedded styles for forum context
.comments-list-embedded {
  margin-bottom: 1rem;
}

.comments-list-embedded-container {
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.comments-loading-embedded {
  padding: 1.5rem 1.25rem;
}

.comments-content-embedded {
  padding: 1rem 1.25rem;
}

.comment-item-spacing-embedded {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f2f5;
}

.comments-empty-embedded {
  padding: 1.5rem 1.25rem;
  background: transparent;
  border: none;
}

.comments-actions-embedded {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background-color: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

// Responsive design
@media (max-width: 768px) {
  .comments-list-container {
    border-radius: 8px;
  }
  
  .comments-header {
    padding: 1rem 1.25rem 0.5rem;
  }
  
  .comments-title {
    font-size: 1rem;
  }
  
  .comments-content {
    padding: 0.75rem 1.25rem;
  }
  
  .comments-empty {
    padding: 1.5rem 1.25rem;
  }
  
  .comments-actions {
    padding: 0.75rem 1.25rem 1rem;
  }
  
  .comments-list-embedded-container {
    border-radius: 6px;
  }
  
  .comments-content-embedded {
    padding: 0.75rem 1rem;
  }
  
  .comments-empty-embedded {
    padding: 1rem;
  }
  
  .comments-actions-embedded {
    padding: 0.5rem 1rem;
  }
}
</style>
